import type { PlaylistItem, Source } from '@jwp/ott-common/types/playlist';
import type { Config } from '@jwp/ott-common/types/config';
import type { Customer } from '@jwp/ott-common/types/account';
import { MANIFEST_TYPE } from '@jwp/ott-common/src/constants';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';

export const getSources = ({
  item,
  baseUrl,
  config,
  user,
  passport,
}: {
  item: PlaylistItem;
  baseUrl: string;
  config: Config;
  user: Customer | null;
  passport: string | null;
}) => {
  const { sources, mediaid, adConfig: mediaAdConfig } = item;
  if (!sources?.length) { return []; }
  const { adConfig: globalAdConfig, siteId, adDeliveryMethod: JWPlayerAdDeliveryMethod } = config;
  const { adConfigLevel, featuredAdDeliveryMethod } = useAppConfig();
  const adDeliveryMethod = featuredAdDeliveryMethod || JWPlayerAdDeliveryMethod;

  const userId = user?.id;
  const hasServerAds = !!globalAdConfig && (item?.is_ssai_enabled ? item?.is_ssai_enabled === 'true' : adDeliveryMethod === 'ssai');
  const adTags = item.tags ?? '';

  return sources.map((source: Source) => {
    const url = new URL(source.file);
    const isBroadcastLiveSource = /\/live\/broadcast\//i.test(source.file) || /\.jwplive\./i.test(source.file);
    const isDRM = (url.searchParams.has('exp') && url.searchParams.has('sig')) || source.drm;

    // Use SSAI URL for configs with server side ads, DRM is not supported yet
    if (hasServerAds && !isDRM && !isBroadcastLiveSource && adConfigLevel && adConfigLevel !== 'none') {
      const isDash = MANIFEST_TYPE.dash === source.type;
      const manifestType = isDash ? 'mpd' : 'm3u8';

      // Base SSAI URL
      url.href = `${baseUrl}/v2/sites/${siteId}/media/${mediaid}/ssai.${manifestType}`;

      // Determine correct ad config ID
      let adConfigId = null;
      switch (adConfigLevel) {
        case 'global':
          adConfigId = globalAdConfig;
          break;
        case 'media':
          adConfigId = mediaAdConfig;
          break;
        case 'globalandmedia':
          adConfigId = mediaAdConfig || globalAdConfig;
          break;
      }

      // Apply ad config ID if found
      if (adConfigId) {
        url.searchParams.set('ad_config_id', adConfigId);
      }

      // Apply cust_params only if adTags exist
      if (adTags) {
        url.searchParams.set('cust_params', adTags);
      }
    }

    // Attach user_id for VOD and BCL SaaS Live Streams
    if (userId) {
      url.searchParams.set('user_id', userId);
    }

    // Attach the passport in all the drm sources as it's needed for the licence request.
    // Passport is only available if Access Bridge is in use.
    const updatedSource = passport ? attachPassportToSourceWithDRM(source, passport) : source;

    return {
      ...updatedSource,
      file: url.toString(),
    };
  });
};

function attachPassportToSourceWithDRM(source: Source, passport: string): Source {
  function updateUrl(urlString: string, passport: string): string {
    const url = new URL(urlString);
    if (!url.searchParams.has('token')) {
      url.searchParams.set('passport', passport);
    }
    return url.toString();
  }

  if (source?.drm) {
    if (source.drm?.playready?.url) {
      source.drm.playready.url = updateUrl(source.drm.playready.url, passport);
    }
    if (source.drm?.widevine?.url) {
      source.drm.widevine.url = updateUrl(source.drm.widevine.url, passport);
    }
    if (source.drm?.fairplay?.processSpcUrl) {
      source.drm.fairplay.processSpcUrl = updateUrl(source.drm.fairplay.processSpcUrl, passport);
    }
  }

  return source;
}
